<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { ShieldX } from '@lucide/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog/index';

interface Props {
    open: boolean;
    action?: string;
    model?: string;
    message?: string;
}

const props = withDefaults(defineProps<Props>(), {
    action: 'read',
    model: 'resource',
    message: '',
});

const { t } = useI18n();

const emit = defineEmits<{
    'update:open': [value: boolean];
}>();

const actionLabel = computed(() => {
    return t(`crud.actions.${props.action}`) || props.action;
});

function goBack() {
    if (window.history.length > 1) {
        window.history.back();
    }
    emit('update:open', false);
}

function goDashboard() {
    router.visit('/crud');
    emit('update:open', false);
}
</script>

<template>
    <AlertDialog :open="open" @update:open="emit('update:open', $event)">
        <AlertDialogContent class="max-w-md">
            <AlertDialogHeader class="text-center sm:text-center">
                <div class="bg-destructive/10 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                    <ShieldX class="text-destructive size-6" />
                </div>
                <AlertDialogTitle class="text-xl">{{ t('crud.access_denied') }}</AlertDialogTitle>
                <AlertDialogDescription class="text-balance">
                    {{ message || t('crud.unauthorized_message') }}
                </AlertDialogDescription>
            </AlertDialogHeader>

            <div class="bg-muted text-muted-foreground rounded-lg p-3 text-center text-sm">
                <p>
                    {{
                        t('crud.unauthorized_action', {
                            action: actionLabel,
                            model: model,
                        })
                    }}
                </p>
            </div>

            <AlertDialogFooter class="sm:justify-center">
                <AlertDialogCancel @click="goBack">{{ t('crud.go_back') }}</AlertDialogCancel>
                <AlertDialogAction @click="goDashboard">{{ t('crud.dashboard') }}</AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
